import React from 'react';

import { Grid, SelectChangeEvent } from '@mui/material';
import { Control, useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { WheelsDirection, WheelsFilters, WheelsZone, WheelsZoneName } from '../../../domain/wheels';
import { FormSelectController } from '../../Checkout/components/CheckoutContactInformation/components/select-controller';

// ********************************************************************************
const CBX_LOCATION_VALUE = 'cbx';
// This component encapsulates the logic of zone and direction selection in the
// wheels service.  The selects "origin" and "destination" are a way to provide a
// user-friendly way to select both the zone and the direction of the service.
// 1. Zone: The relative area where the service is provided.
// 2. Direction: The direction of the service, it can be to cbx or from cbx.
// SEE: domain/wheels-direction.ts
//      domain/wheels-zone.ts
interface Props {
  control: Control<WheelsFilters>;
  zones: WheelsZone[];
}

export const WheelsZonesSelect = ({ zones }: Props) => {
  const { t } = useTranslation();
  const { control, setValue, getValues } = useFormContext<WheelsFilters>();

  const zone = useController({ control, name: 'zone' });
  const direction = useController({ control, name: 'direction' });

  // == Handlers ================================================================
  // Handles the change of the origin or destination fields. As well as the logic
  // of the cross border express location. The next set of rules are applied:
  // 1. The user can't select the same location as origin and destination.
  // 2. When one of the locations is different from CROSS BORDER EXPRESS, the other
  //    must be CROSS BORDER EXPRESS.
  const onLocationChanged = (event: SelectChangeEvent) => {
    const field = event.target.name;
    const selection = event.target.value as WheelsZoneName;

    const oppositeField = field === 'origin' ? 'destination' : 'origin';
    const oppositeFieldSelection = getValues(oppositeField);

    const isSameLocation = selection === oppositeFieldSelection;
    if (isSameLocation) setValue(oppositeField, '');

    const isNotCBXLocation = selection !== CBX_LOCATION_VALUE;
    if (isNotCBXLocation) setValue(oppositeField, CBX_LOCATION_VALUE);

    zone.field.onChange(selection);
    direction.field.onChange(field === 'origin' ? WheelsDirection.ToCBX : WheelsDirection.FromCBX);
  };

  // == Render ==================================================================
  return (
    <Grid container columns={4} spacing={3}>
      <Grid item xs={4} md={2}>
        <FormSelectController
          name="origin"
          label={t('wheels_service.inputs.origin')}
          options={zones.map(({ name, display }) => ({ value: name, label: display }))}
          onChange={onLocationChanged}
          rules={{ required: true }}
        />
      </Grid>
      <Grid item xs={4} md={2}>
        <FormSelectController
          name="destination"
          label={t('wheels_service.inputs.destination')}
          options={zones.map(({ name, display }) => ({ value: name, label: display }))}
          onChange={onLocationChanged}
          rules={{ required: true }}
        />
      </Grid>
    </Grid>
  );
};
